/* eslint-disable max-classes-per-file */
import _ from 'underscore';
import Vue from 'vue';

import { ValidationError } from './validation-error';

class Errors {
  errs = null;

  constructor() {
    this.errs = {};
  }

  /**
   * The number of keys in this error map
   *
   * @returns {Number}
   */
  get numKeys() {
    return Object.keys(this.errs).length;
  }

  /**
   * Get the recursive error count
   *
   * @returns {Number}
   */
  get length() {
    let count = 0;
    Object.values(this.errs).forEach((v) => {
      if (v === null || v === undefined) {
        return;
      }
      if (Array.isArray(v) || v instanceof Errors) {
        count += v.length;
      } else if (v instanceof ValidationError) {
        count += 1;
      } else if (v instanceof Error) {
        count += 1;
      } else {
        count += 1; // just increment for any non-null val
      }
    });
    return count;
  }

  /**
   * Flatten the errors into an array
   *
   * @returns {Array<Error>}
   */
  flatten() {
    let errors = [];
    Object.values(this.errs).forEach((v) => {
      if (v === null || v === undefined) {
        return;
      }
      if (Array.isArray(v)) {
        errors = errors.concat(v);
      } else if (v instanceof Errors) {
        errors = errors.concat(v.flatten());
      } else {
        errors.push(v);
      }
    });
    return errors;
  }

  /**
   * Returns true if there are no errors. Optionally tests only the given key.
   *
   * @param {String} key Key to check
   * @returns {Boolean}
   */
  isEmpty(key) {
    function isEmptyVal(v) {
      return (
        v === null ||
        v === undefined ||
        (Array.isArray(v) && v.length === 0) ||
        (v instanceof Errors && v.isEmpty())
      );
    }

    if (key) {
      return isEmptyVal(this.errs[key]);
    }
    // look for anything which isn't empty and negate it
    return !Object.values(this.errs).find((v) => !isEmptyVal(v));
  }

  /**
   * True if not empty. See also `isEmpty`.
   *
   * @param {String} key
   * @returns {Boolean}
   */
  isNotEmpty(key) {
    return !this.isEmpty(key);
  }

  /**
   * Update the error for the given key and trigger reactive updates.
   *
   * Can be called using a single-param context as well. i.e., all of the
   * following are valid:
   *
   *   update("someKey", errVal);
   *   update(null, errVal);
   *   update(errVal);
   *
   * @param {String} key to update (optional)
   * @param {Object} val error value (or null) (optional)
   */
  update(key, val) {
    if (!_.isString(key)) {
      // use val if given, otherwise assume only key, i.e.,
      //   this.errors.update(err)
      // to update the default key
      Vue.set(this.errs, 'default', val || key);
    } else {
      Vue.set(this.errs, key, val);
    }
  }
}

export { Errors, ValidationError };
